/**
* IBM Confidential
* Licensed Materials - Property of IBM
* (C) Copyright IBM Corp. 2020, 2024 All Rights Reserved.
* US Government Users Restricted Rights - Use, duplication or
* disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
* */

import { slicedToArray as _slicedToArray, toConsumableArray as _toConsumableArray, objectSpread2 as _objectSpread2, extends as _extends } from '../../../../_virtual/_rollupPluginBabelHelpers.js';
import React, { createContext, useState, useCallback, useMemo, useContext } from 'react';
import PropTypes from 'prop-types';
import { ToastNotification as ToastNotification$1 } from '@carbon/react';
import { useTranslation } from '../../../../Utilities/i18n/i18n.js';

var Context = /*#__PURE__*/createContext();
Context.displayName = 'ToastNotificationContext';
var Provider = function Provider(_ref) {
  var children = _ref.children;
  var _useState = useState([]),
    _useState2 = _slicedToArray(_useState, 2),
    notifications = _useState2[0],
    setNotifications = _useState2[1];
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var getDefaultTitle = useCallback(function (kind) {
    var defaultValue = t('Note');
    if (!kind || typeof kind !== 'string') return defaultValue;
    var key = kind.split('-')[0];
    return {
      error: t('Error'),
      success: t('Success'),
      warning: t('Warning'),
      info: defaultValue
    }[key] || defaultValue;
  }, [t]);
  var add = useCallback(function (notification) {
    setNotifications(function (prev) {
      return [].concat(_toConsumableArray(prev), [_objectSpread2({
        key: new Date().toISOString(),
        kind: 'info',
        timeout: 5000,
        statusIconDescription: t('Close'),
        title: getDefaultTitle(notification.kind || 'info'),
        subtitle: null,
        caption: null,
        lowContrast: true
      }, notification)]);
    });
  }, [getDefaultTitle, t]);
  var remove = useCallback(function (key) {
    setNotifications(function (prev) {
      return prev.filter(function (n) {
        return n.key !== key;
      });
    });
  }, []);
  var value = useMemo(function () {
    return {
      add: add
    };
  }, [add]);

  // Make sure toast notifications have the highest z-index. Modal is
  // at 9000 and SidePanel is at 5100.
  return /*#__PURE__*/React.createElement(Context.Provider, {
    value: value
  }, /*#__PURE__*/React.createElement("div", {
    className: "cpc--provider-toaster",
    style: {
      position: 'fixed',
      right: 0,
      top: 0,
      zIndex: 9001
    }
  }, notifications.map(function (notif) {
    return /*#__PURE__*/React.createElement(ToastNotification$1, _extends({
      style: {
        marginBottom: '.5rem',
        position: 'relative'
      },
      onCloseButtonClick: function onCloseButtonClick() {
        return remove(notif.key);
      }
      // eslint-disable-next-line react/jsx-props-no-spreading
    }, notif));
  })), children);
};
Provider.propTypes = {
  children: PropTypes.node.isRequired
};
var useToastNotification = function useToastNotification() {
  return useContext(Context);
};
var ToastNotification = {
  Context: Context,
  Provider: Provider,
  useToastNotification: useToastNotification
};

export { ToastNotification as default };
